/* =====================================================================
 * PluginModal — 외부 앱 연결 플러그인 관리 모달 (OAuth + 토글)
 * ===================================================================== */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styled, { css, keyframes } from 'styled-components';
import {
  resolvePluginCatalog,
  type PluginCatalogEntry,
  type PluginDescriptor,
} from '@auw/plugins';
import { useTenant } from '@auw/tenant';
import { Button } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { ToggleSwitch } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import {
  Globe,
  Check,
  Loader2,
  Zap,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Shield,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import {
  listPlugins,
  verifyProvider,
  updateProviderSettings,
  type PluginInfo,
  type PluginVerifyResponse,
} from '@auw/api-client';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '@auw/stores';
import { queryKeys } from '@auw/workspace-utils';
import {
  clearPendingPluginOAuthFlow,
  clearPluginOAuthCallbackParams,
  getPendingPluginOAuthFlow,
  getPluginOAuthResultIdentity,
  hasPendingPluginOAuthFlow,
  parsePluginOAuthStorageEvent,
  getPluginOAuthChannel,
  readLatestPluginOAuthResult,
  readPluginOAuthCallbackFromLocation,
  type PluginOAuthResultMessage,
} from '@auw/workspace-utils';

/* Plugin metadata (label, logo, catalog copy, advertised tools, MCP
 * connector info, OAuth scope notices) used to live in six parallel
 * lookup tables here. All of it moved into `@auw/plugins` in Phase 4b
 * — `resolvePluginCatalog()` merges the builtin descriptors with
 * tenant overrides and backend state into a single
 * `PluginCatalogEntry[]` the component iterates over. */

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const glassPanelStyles = css`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(18,18,18,0.82)' : 'rgba(255,255,255,0.9)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.58)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 16px 36px rgba(0,0,0,0.28)'
      : '0 14px 32px rgba(0,0,0,0.08)'};
  backdrop-filter: blur(24px) saturate(165%);
  -webkit-backdrop-filter: blur(24px) saturate(165%);
`;

const glassInsetStyles = css`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(30,30,30,0.78)' : 'rgba(255,255,255,0.84)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.52)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 10px 24px rgba(0,0,0,0.18)'
      : '0 8px 20px rgba(0,0,0,0.05)'};
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin: 0 0 14px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.bodyFontSize};
  }
`;

const Viewport = styled.div`
  position: relative;
  min-height: 460px;
  overflow: hidden;
`;

const ViewPane = styled.div<{ $direction: 'forward' | 'back' }>`
  animation: ${({ $direction }) => ($direction === 'forward' ? slideInFromRight : slideInFromLeft)} 220ms
    cubic-bezier(0.22, 1, 0.36, 1);
`;

const SectionLabel = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.01em;
  margin: 0 0 10px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6875rem;
  }
`;

const PluginGrid = styled.div`
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const PluginCard = styled.button<{ $connected: boolean; $selected: boolean }>`
  display: flex;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  ${glassInsetStyles};
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(38,38,38,0.86)' : 'rgba(255,255,255,0.92)'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.7)'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  ${({ $connected, theme }) =>
    $connected &&
    css`
      border-color: ${theme.mode === 'dark' ? 'rgba(16,163,127,0.28)' : 'rgba(16,163,127,0.22)'};
      background: ${theme.mode === 'dark' ? 'rgba(16,163,127,0.16)' : 'rgba(16,163,127,0.12)'};
    `}

  ${({ $selected, theme }) =>
    $selected &&
    css`
      border-color: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)'};
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.96)'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 14px 30px rgba(0,0,0,0.24)'
        : '0 12px 28px rgba(0,0,0,0.08)'};
    `}
`;

const PluginIconWrap = styled.div<{ $bg: string; $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #f5f5f7, #eaeaed)'};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.35)'
      : '0 1px 4px rgba(0,0,0,0.06)'};
`;

const PluginInfoArea = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PluginTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PluginName = styled.span`
  display: block;
  flex: 0 1 auto;
  font-size: ${({ theme }) => theme.modal.nameFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.35;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.nameFontSize};
  }
`;

const NameStatusChip = styled.span<{ $connected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.01em;

  ${({ $connected, theme }) =>
    $connected
      ? css`
          color: ${theme.colors.success};
          background: ${theme.mode === 'dark' ? 'rgba(16, 163, 127, 0.16)' : 'rgba(16, 163, 127, 0.12)'};
        `
      : css`
          color: ${theme.colors.textMuted};
          background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.72)'};
        `}
`;

const PluginDesc = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.35;
  margin-top: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.descFontSize};
  }
`;

const CardChevron = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.mode === 'dark' ? '#ffffff' : '#000000'};
  margin-left: 4px;
  flex-shrink: 0;
  opacity: 1;
  transform: ${({ $active }) => ($active ? 'translateX(1px)' : 'none')};
  transition: opacity 0.2s ease, transform 0.2s ease;
`;

const SpinningIcon = styled.div`
  display: flex;
  animation: ${spin} 1s linear infinite;
`;

/* ---- 연결 상세 패널 ---- */

const DetailPanel = styled.div`
  padding: 16px;
  ${glassPanelStyles};
  border-radius: 14px;
  animation: ${fadeIn} 200ms ease-out;
`;

const McpNoticeInline = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
  opacity: 0.8;
`;

const DetailDescription = styled.p`
  margin: 0 0 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.bodyFontSize};
  }
`;

const DetailInfoSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 16px 0 8px;
  letter-spacing: 0.01em;
`;

const DetailInfoSection = styled.div`
  margin-bottom: 14px;
  ${glassInsetStyles};
  border-radius: 12px;
  overflow: hidden;
`;

const DetailInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 14px;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 4px;
    padding: 11px 12px;
  }
`;

const DetailInfoLabel = styled.span`
  width: 112px;
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: auto;
    font-size: 0.75rem;
  }
`;

const DetailInfoValue = styled.div`
  min-width: 0;
  flex: 1;
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.bodyFontSize};
  }
`;

const DetailInfoLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration: underline;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const DetailTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.modal.nameFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DetailActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(248,113,113,0.12)' : 'rgba(239,68,68,0.1)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(248,113,113,0.24)' : 'rgba(239,68,68,0.16)'};
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.error};
  margin-top: 8px;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(16,163,127,0.12)' : 'rgba(16,163,127,0.1)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(16,163,127,0.24)' : 'rgba(16,163,127,0.16)'};
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.success};
  margin-top: 8px;
`;

const PendingMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.warningBg};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(251,191,36,0.28)' : 'rgba(245,158,11,0.2)'};
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.warning : '#b45309')};
  margin-top: 8px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;
`;

const StatusDot = styled.span<{ $ok: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $ok, theme }) => ($ok ? theme.colors.success : theme.colors.textMuted)};
`;

const ScopeNotice = styled.div`
  margin-bottom: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.1)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(59,130,246,0.24)' : 'rgba(59,130,246,0.18)'};
`;

const ScopeNoticeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ScopeNoticeText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.modal.descFontSize};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0 2px;
  border-top: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  margin-top: 12px;
`;

const ToggleLabel = styled.div`
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.descFontSize};
  }
`;

const CancelOAuthButton = styled.button`
  padding: 5px 14px;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(248,113,113,0.1)' : 'rgba(239,68,68,0.06)'};
    color: ${({ theme }) => theme.colors.error ?? '#ef4444'};
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(248,113,113,0.25)' : 'rgba(239,68,68,0.2)'};
  }
`;

const TeamInfo = styled.div`
  font-size: ${({ theme }) => theme.modal.descFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 10px;

  strong {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/* ---- 도구 목록 섹션 ---- */

const ToolsSection = styled.div`
  margin-top: 16px;
  padding: 14px;
  ${glassInsetStyles};
  border-radius: 12px;
`;

const ToolsSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;
  letter-spacing: 0.01em;
`;

const ToolsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ToolItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 5px 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  }
`;

const ToolNameChip = styled.span`
  flex-shrink: 0;
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  letter-spacing: -0.02em;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const ToolDescription = styled.span`
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.4;
`;

const McpToolGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const McpToolGroupLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const McpToolChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const McpToolChip = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  letter-spacing: -0.02em;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const McpTrustNotice = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const McpTrustAuthor = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
  a {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

const McpTrustText = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
`;

const McpShowMoreButton = styled.button`
  display: inline-block;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const McpDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
`;

const McpDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const McpDetailLabel = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const McpDetailValue = styled.span`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  a {
    color: ${({ theme }) => theme.colors.accent};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

/* ---- 메시지 발신자 설정 ---- */

const SendAsSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const SendAsSectionTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 8px;
  letter-spacing: 0.01em;
`;

const SendAsOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SendAsOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')
      : (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')};
  background: ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff')
      : 'transparent'};
  box-shadow: ${({ $active, theme }) =>
    $active
      ? (theme.mode === 'dark'
          ? '0 1px 4px rgba(0,0,0,0.2)'
          : '0 1px 4px rgba(0,0,0,0.05)')
      : 'none'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 1px 4px rgba(0,0,0,0.2)'
        : '0 1px 4px rgba(0,0,0,0.05)'};
  }
`;

const SendAsRadio = styled.div<{ $checked: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme, $checked }) => $checked ? theme.colors.accent : theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme, $checked }) => $checked ? theme.colors.accent : 'transparent'};
    transition: background 0.15s ease;
  }
`;

const SendAsOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const SendAsOptionLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
`;

const SendAsOptionDesc = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
`;

/* ---- 로딩 상태 ---- */

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.modal.bodyFontSize};
`;

/* ------------------------------------------------------------------ */
/* 플러그인 아이콘 렌더링                                                */
/* ------------------------------------------------------------------ */

const PluginLogo = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  border-radius: 22%;
`;

/** Render the descriptor's icon.
 *
 *  Builtin descriptors always carry a `logoSrc` (Slack, Notion, …);
 *  backend-only custom MCP connectors produced by `@auw/plugins`'
 *  fallback path have an empty `logoSrc` and fall back to the
 *  generic globe glyph historically used by this modal's
 *  `fallbackMeta`. */
const renderPluginIcon = (descriptor: PluginDescriptor): React.ReactNode => {
  if (descriptor.meta.logoSrc) {
    return (
      <PluginLogo src={descriptor.meta.logoSrc} alt={descriptor.meta.logoAlt} />
    );
  }
  return <Globe size={20} />;
};

/* All of `PLUGIN_CATALOG_INFO`, `getPluginCatalogInfo`, and
 * `PLUGIN_SCOPE_NOTICES` moved into the `@auw/plugins` builtin
 * descriptors. `renderDetailPanel()` now reads
 * `descriptor.catalog.{categoryKey, featuresKey, descriptionKey,
 * developer, websiteUrl, privacyPolicyUrl}` and
 * `descriptor.scopeNotice` directly. */

const formatInfoLinkLabel = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

function resolveMaybeTranslated(
  t: (key: string) => string,
  literal: string | undefined,
  key: string | undefined,
): string | null {
  if (literal?.trim()) return literal.trim();
  if (key?.trim()) return t(key.trim());
  return null;
}

function resolveConnectionMode(plugin: PluginInfo, descriptor: PluginDescriptor): string {
  const overrideMode = descriptor.ux?.connectionMode;
  if (overrideMode) return overrideMode;
  const supported = plugin.supported_auth_schemes || [];
  if (supported.includes('oauth')) return 'oauth';
  if (supported.includes('api_key')) return 'api_key';
  if (supported.includes('tenant_identity')) return 'tenant_identity';
  return 'none';
}

function isPolicyManagedConnection(mode: string): boolean {
  return mode === 'tenant_identity' || mode === 'managed';
}

/* Plugin tool catalogs, MCP connector details and the i18n tool-key
 * builder have all been folded into the `@auw/plugins` builtin
 * descriptors. The modal now reads `descriptor.tools` and
 * `descriptor.mcp` directly and asks `tenantOverride.tools` /
 * `tenantOverride.mcp` to append or replace per tenant. */

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface PluginContentProps {
  /** true일 때 플러그인 목록 로드 (모달에서는 open, 페이지에서는 항상 true) */
  isActive?: boolean;
  /** 초기에 자동 선택할 플러그인 ID (딥링크) */
  initialPluginId?: string | null;
}

type ActionMessageState =
  | { type: 'success'; text: string }
  | { type: 'error'; text: string }
  | { type: 'pending'; text: string };

export const PluginContent: React.FC<PluginContentProps> = ({ isActive = true, initialPluginId }) => {
  const { t } = useTranslation(['widget', 'common']);
  const addToast = useChatStore((s) => s.addToast);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  // 화면 전환 상태
  const [selectedPlugin, setSelectedPlugin] = useState<string | null>(null);
  const [viewDirection, setViewDirection] = useState<'forward' | 'back'>('forward');

  // Slack 관련 상태
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<PluginVerifyResponse | null>(null);
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [connecting, setConnecting] = useState(() => hasPendingPluginOAuthFlow());
  const [disconnecting, setDisconnecting] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessageState | null>(null);

  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const timeoutTimerRef = useRef<number | null>(null);
  const handledOAuthResultRef = useRef('');

  const cleanupOAuthWait = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    popupRef.current = null;
  }, []);

  const {
    data: pluginsData,
    isFetching: pluginsFetching,
    isError: pluginsError,
    refetch: refetchPlugins,
  } = useQuery<PluginInfo[]>({
    queryKey: queryKeys.plugins.list(),
    queryFn: async () => {
      const res = await listPlugins();
      return res.plugins;
    },
    enabled: isActive,
    refetchOnWindowFocus: true,
  });

  const plugins = pluginsData ?? [];
  const loading = pluginsFetching && !pluginsData;
  const error = pluginsError ? t('pluginDetail.connection.loadPluginsFailed') : null;

  const tenant = useTenant();
  const tenantPlugins = tenant?.plugins;

  /* Merge the backend snapshot with the builtin descriptors + tenant
   * overrides exactly once per render. Every downstream lookup
   * (label, icon, catalog copy, tools, scope notice, mcp info) goes
   * through this catalog instead of a separate inline table. */
  const catalogEntries = useMemo(
    () =>
      resolvePluginCatalog({
        backendPlugins: plugins,
        hiddenPluginIds: tenantPlugins?.hiddenIds,
        order: tenantPlugins?.order,
        overrides: tenantPlugins?.overrides,
      }),
    [plugins, tenantPlugins],
  );

  const catalogByBackendId = useMemo(() => {
    const m = new Map<string, PluginCatalogEntry>();
    for (const e of catalogEntries) {
      if (e.backend) m.set(e.backend.id, e);
    }
    return m;
  }, [catalogEntries]);

  const descriptorFor = useCallback(
    (pluginId: string): PluginDescriptor | undefined =>
      catalogByBackendId.get(pluginId)?.descriptor,
    [catalogByBackendId],
  );

  /* ---- 플러그인 목록 로드 ---- */
  const fetchPlugins = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.plugins.list() });
    await refetchPlugins();
  }, [queryClient, refetchPlugins]);

  const applyOAuthResult = useCallback((message: PluginOAuthResultMessage) => {
    const resultIdentity = getPluginOAuthResultIdentity(message);
    if (handledOAuthResultRef.current === resultIdentity) return;
    handledOAuthResultRef.current = resultIdentity;

    const pluginLabel = descriptorFor(message.plugin)?.label ?? message.plugin;
    clearPendingPluginOAuthFlow(message.flowId);
    cleanupOAuthWait();

    if (message.status === 'connected') {
      addToast({
        type: 'success',
        title: t('pluginDetail.connection.connectedToast', { plugin: pluginLabel }),
        description: t('pluginDetail.connection.canUseTools'),
        duration: 3000,
      });
      setActionMessage({ type: 'success', text: t('pluginDetail.connection.connectedSuccess', { plugin: pluginLabel }) });
      void fetchPlugins();
    } else {
      const err = message.error || t('pluginDetail.connection.unknownError');
      setActionMessage({ type: 'error', text: t('pluginDetail.connection.connectFailed', { plugin: pluginLabel, error: err }) });
    }

    setConnecting(false);
    clearPluginOAuthCallbackParams();
  }, [addToast, cleanupOAuthWait, fetchPlugins, descriptorFor, t]);

  useEffect(() => {
    if (isActive) {
      fetchPlugins();
      if (initialPluginId) {
        setSelectedPlugin(initialPluginId);
        setViewDirection('forward');
      } else {
        setSelectedPlugin(null);
        setViewDirection('forward');
      }
      setVerifyResult(null);
      setActionMessage(null);
    }
  }, [isActive, fetchPlugins, initialPluginId]);

  // OAuth 콜백 결과 처리
  useEffect(() => {
    const callback = readPluginOAuthCallbackFromLocation();
    if (!callback || callback.popupExpected) return;

    const pending = getPendingPluginOAuthFlow();
    if (pending && callback.flowId && pending.flowId !== callback.flowId) return;
    if (pending && pending.plugin !== callback.plugin) return;

    applyOAuthResult(callback);
  }, [applyOAuthResult]);

  // BroadcastChannel + storage 리스너 — 새 탭에서 보낸 OAuth 결과 수신
  useEffect(() => {
    const handleExternalResult = (message: PluginOAuthResultMessage | null) => {
      if (!message) return;
      const pending = getPendingPluginOAuthFlow();
      if (!pending) return;
      if (message.flowId && pending.flowId !== message.flowId) return;
      if (pending.plugin !== message.plugin) return;
      applyOAuthResult(message);
    };

    let ch: BroadcastChannel | null = null;
    const channelHandler = (event: MessageEvent<PluginOAuthResultMessage>) => {
      handleExternalResult(event.data);
    };
    try {
      ch = new BroadcastChannel(getPluginOAuthChannel());
    } catch {
      ch = null;
    }

    const storageHandler = (event: StorageEvent) => {
      handleExternalResult(parsePluginOAuthStorageEvent(event));
    };

    if (ch) {
      ch.addEventListener('message', channelHandler);
    }
    window.addEventListener('storage', storageHandler);
    handleExternalResult(readLatestPluginOAuthResult());

    return () => {
      window.removeEventListener('storage', storageHandler);
      if (!ch) return;
      ch.removeEventListener('message', channelHandler);
      ch.close();
    };
  }, [applyOAuthResult]);

  useEffect(() => {
    return () => { cleanupOAuthWait(); };
  }, [cleanupOAuthWait]);

  /* ---- OAuth 연결 시작 ---- */
  const handleConnect = useCallback(async (pluginId: string) => {
    setConnecting(true);
    setActionMessage(null);
    try {
      throw new Error(t('pluginDetail.connection.oauthUrlFailed', {
        plugin: descriptorFor(pluginId)?.label ?? pluginId,
      }));
    } catch (e: any) {
      console.error(`[PluginModal] ${pluginId} connect failed:`, e);
      const pluginLabel = descriptorFor(pluginId)?.label ?? pluginId;
      cleanupOAuthWait();
      setActionMessage({
        type: 'error',
        text: e.message || t('pluginDetail.connection.oauthUrlFailed', { plugin: pluginLabel }),
      });
      setConnecting(false);
    }
  }, [cleanupOAuthWait, descriptorFor, t]);

  const handleCancelOAuth = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    clearPendingPluginOAuthFlow();
    cleanupOAuthWait();
    setConnecting(false);
    setActionMessage(null);
  }, [cleanupOAuthWait]);

  /* ---- 연결 해제 ---- */
  const handleDisconnect = useCallback(async (pluginId: string) => {
    const target = plugins.find((p) => p.id === pluginId);
    if (target && !target.connected) {
      setActionMessage({
        type: 'error',
        text: t('pluginDetail.connection.noConnectedAccount'),
      });
      return;
    }
    setDisconnecting(true);
    setActionMessage(null);
    try {
      throw new Error(t('pluginDetail.connection.disconnectFailed'));
    } catch (e: any) {
      console.error(`[PluginModal] ${pluginId} disconnect failed:`, e);
      setActionMessage({
        type: 'error',
        text: e.message || t('pluginDetail.connection.disconnectFailed'),
      });
    } finally {
      setDisconnecting(false);
    }
  }, [plugins, t]);

  /* ---- 연결 토글 ---- */
  const handleToggle = useCallback(
    (pluginId: string, checked: boolean) => {
      if (!checked) {
        // OFF → 해제
        handleDisconnect(pluginId);
      } else {
        // ON → 연결
        handleConnect(pluginId);
      }
    },
    [handleConnect, handleDisconnect],
  );

  /* ---- 연결 검증 ---- */
  const handleVerify = useCallback(async (pluginId: string) => {
    const descriptor = descriptorFor(pluginId);
    if (descriptor?.ux?.hideVerifyAction ||
        isPolicyManagedConnection(descriptor?.ux?.connectionMode ?? 'oauth')) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await verifyProvider(pluginId);
      setVerifyResult(result);
    } catch (e: any) {
      console.error(`[PluginModal] ${pluginId} verify failed:`, e);
      setVerifyResult({
        success: false,
        transport: 'unknown',
        tool_count: 0,
        tools: [],
        error: e.message || t('pluginDetail.connection.verifyFailed'),
      });
    } finally {
      setVerifying(false);
    }
  }, [descriptorFor, t]);

  /* ---- 발신자 설정 변경 ---- */
  const handleSendAsChange = useCallback(async (pluginId: string, value: string) => {
    try {
      await updateProviderSettings(pluginId, { send_as: value });
      await fetchPlugins();
    } catch (e: any) {
      console.error(`[PluginModal] ${pluginId} send_as update failed:`, e);
      setActionMessage({
        type: 'error',
        text: e.message || t('pluginDetail.connection.settingsUpdateFailed'),
      });
    }
  }, [fetchPlugins]);

  /* ---- 플러그인 카드 클릭 ---- */
  const handlePluginClick = (plugin: PluginInfo) => {
    setViewDirection('forward');
    setSelectedPlugin(plugin.id);
    setVerifyResult(null);
    setActionMessage(null);
    setToolsExpanded(false);
    if (plugin.connected) {
      handleVerify(plugin.id);
    }
  };

  const handleBackToList = useCallback(() => {
    setViewDirection('back');
    setSelectedPlugin(null);
    setVerifyResult(null);
    setActionMessage(null);
  }, []);

  /* ---- 필터링 ----
   *
   * The catalog already respects tenant ordering and hidden ids.
   * We only apply the live search query on top of that. */
  const filteredEntries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return catalogEntries.filter((e) => e.backend !== null);
    }
    return catalogEntries.filter((e) => {
      if (!e.backend) return false;
      return (
        e.descriptor.label.toLowerCase().includes(q) ||
        e.backend.name.toLowerCase().includes(q) ||
        e.backend.description.toLowerCase().includes(q)
      );
    });
  }, [catalogEntries, search]);

  const connectedEntries = filteredEntries.filter((e) => e.connected);
  const availableEntries = filteredEntries.filter((e) => !e.connected);
  const selectedEntry = selectedPlugin ? catalogByBackendId.get(selectedPlugin) ?? null : null;
  const selectedPluginData = selectedEntry?.backend ?? null;

  /* ---- 연결 상세 패널 ---- */
  const renderDetailPanel = (entry: PluginCatalogEntry) => {
    const plugin = entry.backend;
    if (!plugin) return null;
    const descriptor = entry.descriptor;
    const connectionMode = resolveConnectionMode(plugin, descriptor);
    const isPolicyManaged = isPolicyManagedConnection(connectionMode);
    const uxStatusLabel = resolveMaybeTranslated(
      t,
      descriptor.ux?.statusLabel,
      descriptor.ux?.statusLabelKey,
    );
    const detailNotice = resolveMaybeTranslated(
      t,
      descriptor.ux?.detailNotice,
      descriptor.ux?.detailNoticeKey,
    );
    const supportedAuth = plugin.supported_auth_schemes || [];
    const supportsOAuthConnect = supportedAuth.includes('oauth') && !descriptor.ux?.hideConnectActions;
    const isConnected = plugin.connected;
    const hasOAuth = plugin.oauth_configured;
    const canVerifyConnection = isConnected && !descriptor.ux?.hideVerifyAction && !isPolicyManaged;
    const tools = descriptor.tools;
    const scopeNotice = descriptor.scopeNotice ?? null;
    const mcpInfo = descriptor.mcp ?? null;
    const detailInfoRows = [
      descriptor.catalog.categoryKey
        ? {
            key: 'category',
            label: t('pluginDetail.ui.category'),
            value: t(descriptor.catalog.categoryKey),
          }
        : null,
      descriptor.catalog.featuresKey
        ? {
            key: 'features',
            label: t('pluginDetail.ui.functionsLabel'),
            value: t(descriptor.catalog.featuresKey),
          }
        : null,
      descriptor.catalog.developer
        ? {
            key: 'developer',
            label: t('pluginDetail.ui.developer'),
            value: descriptor.catalog.developer,
          }
        : null,
      descriptor.catalog.websiteUrl
        ? {
            key: 'website',
            label: t('pluginDetail.ui.website'),
            value: (
              <DetailInfoLink
                href={descriptor.catalog.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatInfoLinkLabel(descriptor.catalog.websiteUrl)}
                <ExternalLink size={13} />
              </DetailInfoLink>
            ),
          }
        : null,
      descriptor.catalog.privacyPolicyUrl
        ? {
            key: 'privacy',
            label: t('pluginDetail.ui.privacyPolicy'),
            value: (
              <DetailInfoLink
                href={descriptor.catalog.privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatInfoLinkLabel(descriptor.catalog.privacyPolicyUrl)}
                <ExternalLink size={13} />
              </DetailInfoLink>
            ),
          }
        : null,
    ].filter(Boolean);

    return (
      <DetailPanel>
        <DetailHeader>
          <DetailTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToList}
              aria-label={t('pluginDetail.ui.backToList')}
            >
              <ArrowLeft size={16} />
            </Button>
            <PluginIconWrap
              $bg={descriptor.meta.accentBackground}
              $color={descriptor.meta.accentColor}
            >
              {renderPluginIcon(descriptor)}
            </PluginIconWrap>
            {descriptor.label}
          </DetailTitle>

          <DetailActions>
            {canVerifyConnection && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleVerify(plugin.id)}
                disabled={verifying}
                isLoading={verifying}
                leftIcon={<RefreshCw size={14} />}
              >
                {verifying ? t('pluginDetail.ui.verifying') : t('pluginDetail.ui.verifyConnection')}
              </Button>
            )}
          </DetailActions>
        </DetailHeader>

        <DetailDescription>
          {t(descriptor.catalog.descriptionKey)}
          {' · '}
          {t('pluginDetail.ui.detailDescription')}
          {mcpInfo && (
            <McpNoticeInline>{t('pluginDetail.ui.mcpTrustNotice')}</McpNoticeInline>
          )}
        </DetailDescription>

        {scopeNotice && (
          <ScopeNotice>
            <ScopeNoticeTitle>
              <Shield size={14} />
              {t(scopeNotice.titleKey)}
            </ScopeNoticeTitle>
            <ScopeNoticeText>{t(scopeNotice.descriptionKey)}</ScopeNoticeText>
          </ScopeNotice>
        )}

        {/* 연결 정보 */}
        {isConnected && plugin.team_name && (
          <TeamInfo>
            <strong>{t('pluginDetail.ui.workspace')}</strong> {plugin.team_name}
          </TeamInfo>
        )}

        {/* 연결 상태 */}
        {(isConnected || uxStatusLabel) && (
          <StatusRow>
            <StatusDot $ok />
            {uxStatusLabel ?? t('pluginDetail.ui.statusConnected')}
          </StatusRow>
        )}

        {detailNotice && (
          <StatusRow>
            <Shield size={14} />
            {detailNotice}
          </StatusRow>
        )}

        {/* OAuth 미설정 안내 */}
        {supportsOAuthConnect && !isConnected && !hasOAuth && plugin.transport !== 'stdio' && plugin.transport !== 'sse' && (
          <StatusRow>
            <Shield size={14} />
            {t('pluginDetail.ui.oauthNotConfigured')}
          </StatusRow>
        )}

        {/* 검증 결과 */}
        {verifyResult && (
          <>
            <StatusRow>
              <StatusDot $ok={verifyResult.success} />
              {verifyResult.success
                ? t('pluginDetail.ui.verifySuccess', { count: verifyResult.tool_count })
                : t('pluginDetail.ui.verifyProblem')}
            </StatusRow>

            {verifyResult.error && (
              <ErrorMessage>
                <AlertCircle size={14} />
                {verifyResult.error}
              </ErrorMessage>
            )}
          </>
        )}

        {/* 액션 메시지 */}
        {actionMessage && (
          actionMessage.type === 'success' ? (
            <SuccessMessage role="status" aria-live="polite">
              <Check size={14} />
              {actionMessage.text}
            </SuccessMessage>
          ) : actionMessage.type === 'pending' ? (
            <PendingMessage role="status" aria-live="polite">
              <SpinningIcon><Loader2 size={14} /></SpinningIcon>
              {actionMessage.text}
            </PendingMessage>
          ) : (
            <ErrorMessage role="alert">
              <AlertCircle size={14} />
              {actionMessage.text}
            </ErrorMessage>
          )
        )}

        {/* 연결 토글 */}
        {supportsOAuthConnect && (isConnected || hasOAuth || connecting) && (
          <ToggleRow>
            <ToggleLabel>
              {connecting ? t('pluginDetail.ui.waitingAuth') : isConnected ? t('pluginDetail.ui.statusConnected') : t('pluginDetail.ui.notConnected')}
            </ToggleLabel>
            {connecting ? (
              <CancelOAuthButton type="button" onClick={handleCancelOAuth}>
                {t('pluginDetail.ui.cancelConnection')}
              </CancelOAuthButton>
            ) : (
              <ToggleSwitch
                checked={isConnected}
                onChange={(checked) => handleToggle(plugin.id, checked)}
                loading={disconnecting}
                disabled={!isConnected && !supportsOAuthConnect}
                size="sm"
              />
            )}
          </ToggleRow>
        )}

        {/* 메시지 발신자 설정 (Slack + 연결됨 + user token 보유) */}
        {isConnected && plugin.id === 'slack' && plugin.has_user_token && (
          <SendAsSection>
            <SendAsSectionTitle>{t('pluginDetail.ui.messageSender')}</SendAsSectionTitle>
            <SendAsOptions>
              <SendAsOption
                $active={(plugin.send_as || 'user') === 'user'}
                onClick={() => handleSendAsChange(plugin.id, 'user')}
              >
                <SendAsRadio $checked={(plugin.send_as || 'user') === 'user'} />
                <SendAsOptionContent>
                  <SendAsOptionLabel>{t('pluginDetail.ui.sendAsUser')}</SendAsOptionLabel>
                  <SendAsOptionDesc>{t('pluginDetail.ui.sendAsUserDesc')}</SendAsOptionDesc>
                </SendAsOptionContent>
              </SendAsOption>
              <SendAsOption
                $active={(plugin.send_as || 'user') === 'bot'}
                onClick={() => handleSendAsChange(plugin.id, 'bot')}
              >
                <SendAsRadio $checked={(plugin.send_as || 'user') === 'bot'} />
                <SendAsOptionContent>
                  <SendAsOptionLabel>{t('pluginDetail.ui.sendAsBot')}</SendAsOptionLabel>
                  <SendAsOptionDesc>{t('pluginDetail.ui.sendAsBotDesc')}</SendAsOptionDesc>
                </SendAsOptionContent>
              </SendAsOption>
            </SendAsOptions>
          </SendAsSection>
        )}

        {detailInfoRows.length > 0 && (
          <>
            <DetailInfoSectionTitle>{t('pluginDetail.ui.information')}</DetailInfoSectionTitle>
            <DetailInfoSection>
              {detailInfoRows.map((row) => (
                <DetailInfoRow key={row!.key}>
                  <DetailInfoLabel>{row!.label}</DetailInfoLabel>
                  <DetailInfoValue>{row!.value}</DetailInfoValue>
                </DetailInfoRow>
              ))}
            </DetailInfoSection>
          </>
        )}

        {/* 도구 목록 */}
        {(() => {
          const isMcpPlugin = !!mcpInfo;
          const activeTools = (verifyResult?.success && verifyResult.tools.length > 0)
            ? verifyResult.tools.map((t) => t.name)
            : tools.map((t) => t.name);
          const maxVisible = mcpInfo?.defaultToolsVisible ?? 999;
          const visibleTools = toolsExpanded ? activeTools : activeTools.slice(0, maxVisible);
          const hiddenCount = activeTools.length - maxVisible;

          if (activeTools.length === 0) return null;

          return (
            <>
              {isMcpPlugin ? (
                <ToolsSection>
                  <ToolsSectionTitle>
                    {t('pluginDetail.ui.availableTools')}
                    <span style={{ marginLeft: 6, opacity: 0.6 }}>{activeTools.length}</span>
                  </ToolsSectionTitle>
                  <McpToolChipGrid>
                    {visibleTools.map((name) => (
                      <McpToolChip key={name}>{name}</McpToolChip>
                    ))}
                  </McpToolChipGrid>
                  {!toolsExpanded && hiddenCount > 0 && (
                    <McpShowMoreButton onClick={() => setToolsExpanded(true)}>
                      +{hiddenCount}{t('pluginDetail.ui.showMore') || '개 더 보기'}
                    </McpShowMoreButton>
                  )}
                  {toolsExpanded && hiddenCount > 0 && (
                    <McpShowMoreButton onClick={() => setToolsExpanded(false)}>
                      {t('pluginDetail.ui.showLess') || '접기'}
                    </McpShowMoreButton>
                  )}
                </ToolsSection>
              ) : (
                <ToolsSection>
                  <ToolsSectionTitle>{t('pluginDetail.ui.availableTools')}</ToolsSectionTitle>
                  <ToolsGrid>
                    {tools.map((tool) => (
                      <ToolItem key={tool.name}>
                        <ToolNameChip>{tool.name}</ToolNameChip>
                        <ToolDescription>{t(tool.descriptionKey)}</ToolDescription>
                      </ToolItem>
                    ))}
                  </ToolsGrid>
                </ToolsSection>
              )}



            </>
          );
        })()}

        {/* 미구현 플러그인 안내 */}
        {!supportsOAuthConnect && !plugin.configured && !isPolicyManaged && (
          <StatusRow style={{ marginTop: 4 }}>
            <Zap size={14} />
            {t('pluginDetail.ui.pluginInPreparation')}
          </StatusRow>
        )}
      </DetailPanel>
    );
  };

  /* ---- 카드 렌더 ---- */
  const renderCard = (entry: PluginCatalogEntry) => {
    const p = entry.backend;
    if (!p) return null;
    const descriptor = entry.descriptor;
    const isSelected = selectedPlugin === p.id;
    const statusLabel = resolveMaybeTranslated(
      t,
      descriptor.ux?.statusLabel,
      descriptor.ux?.statusLabelKey,
    );
    const isPresentedAvailable = p.connected || Boolean(statusLabel);

    return (
      <React.Fragment key={p.id}>
        <PluginCard
          $connected={isPresentedAvailable}
          $selected={isSelected}
          onClick={() => handlePluginClick(p)}
        >
          <PluginIconWrap
            $bg={descriptor.meta.accentBackground}
            $color={descriptor.meta.accentColor}
          >
            {renderPluginIcon(descriptor)}
          </PluginIconWrap>
          <PluginInfoArea>
            <PluginTopRow>
              <PluginName>{descriptor.label}</PluginName>
              <NameStatusChip $connected={isPresentedAvailable}>
                {statusLabel ?? (p.connected ? t('pluginDetail.ui.statusConnected') : t('pluginDetail.ui.statusDisconnected'))}
              </NameStatusChip>
            </PluginTopRow>
            <PluginDesc>{t(descriptor.catalog.descriptionKey)}</PluginDesc>
          </PluginInfoArea>
          <CardChevron $active={isSelected}>
            <ChevronRight size={14} />
          </CardChevron>
        </PluginCard>
      </React.Fragment>
    );
  };

  return (
    <Viewport>
      {/* 로딩 */}
      {loading ? (
        <LoadingWrap>
          <SpinningIcon><Loader2 size={24} /></SpinningIcon>
          {t('pluginDetail.connection.loadingPlugins')}
        </LoadingWrap>
      ) : (
        <ViewPane
          key={`${selectedPluginData ? `detail-${selectedPluginData.id}` : 'list'}-${viewDirection}`}
          $direction={viewDirection}
        >
          {/* 목록 화면 */}
          {!selectedPluginData && (
            <>
              <Description>
                {t('pluginDetail.ui.description')}
              </Description>

              <div style={{ marginBottom: 20 }}>
                <Input
                  variant="search"
                  placeholder={t('plugin.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* 에러 */}
              {error && (
                <ErrorMessage style={{ marginBottom: 16 }}>
                  <AlertCircle size={14} />
                  {error}
                </ErrorMessage>
              )}

              {/* 연결됨 */}
              {connectedEntries.length > 0 && (
                <>
                  <SectionLabel>{t('pluginDetail.ui.sectionConnected')}</SectionLabel>
                  <PluginGrid>
                    {connectedEntries.map(renderCard)}
                  </PluginGrid>
                </>
              )}

              {/* 사용 가능 */}
              {availableEntries.length > 0 && (
                <>
                  <SectionLabel>{t('pluginDetail.ui.sectionAvailable')}</SectionLabel>
                  <PluginGrid>
                    {availableEntries.map(renderCard)}
                  </PluginGrid>
                </>
              )}

              {/* 검색 결과 없음 */}
              {filteredEntries.length === 0 && !error && (
                <EmptyState description={t('pluginDetail.ui.noSearchResults')} />
              )}
            </>
          )}

          {/* 상세 화면 */}
          {selectedEntry && renderDetailPanel(selectedEntry)}
        </ViewPane>
      )}
    </Viewport>
  );
};

/* ------------------------------------------------------------------ */
/* PluginModal — 모달 래퍼 (기존 호환용)                                  */
/* ------------------------------------------------------------------ */

interface PluginModalProps {
  open: boolean;
  onClose: () => void;
}

export const PluginModal: React.FC<PluginModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation('widget');
  return (
    <Modal open={open} onClose={onClose} title={t('plugin.title')} width="760px">
      <PluginContent isActive={open} />
    </Modal>
  );
};
